<template>
  <div class="print-price">
    <el-card>
      <template #header>
        <div class="card-header">
          <span>打印价格设置</span>
          <div>
            <el-button type="success" @click="showAddDialog">新增规格</el-button>
            <el-button type="primary" @click="handleSaveAll">保存全部</el-button>
          </div>
        </div>
      </template>
      
      <el-table :data="priceList" style="width: 100%" v-loading="loading">
        <el-table-column prop="paper_size" label="纸张规格" />
        <el-table-column prop="side_type" label="单双面">
          <template #default="{ row }">
            {{ row.side_type === 'single' ? '单面' : '双面' }}
          </template>
        </el-table-column>
        <el-table-column prop="color_type" label="颜色">
          <template #default="{ row }">
            {{ row.color_type === 'black_white' ? '黑白' : '彩色' }}
          </template>
        </el-table-column>
        <el-table-column prop="price" label="价格（元/页）">
          <template #default="{ row }">
            <el-input-number v-model="row.price" :min="0" :precision="2" :step="0.1" />
          </template>
        </el-table-column>
        <el-table-column prop="member_price" label="会员价（元/页）">
          <template #default="{ row }">
            <el-input-number v-model="row.member_price" :min="0" :precision="2" :step="0.1" />
          </template>
        </el-table-column>
        <el-table-column label="操作" width="100">
          <template #default="{ $index }">
            <el-button type="danger" size="small" @click="handleDelete($index)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
      
      <el-empty v-if="priceList.length === 0 && !loading" description="暂无价格数据" />
    </el-card>
    
    <!-- 新增规格对话框 -->
    <el-dialog v-model="dialogVisible" title="新增打印规格" width="500px">
      <el-form :model="newSpec" label-width="100px">
        <el-form-item label="纸张规格">
          <el-select v-model="newSpec.paper_size" placeholder="请选择">
            <el-option label="A3" value="A3" />
            <el-option label="A4" value="A4" />
            <el-option label="A5" value="A5" />
          </el-select>
        </el-form-item>
        <el-form-item label="单双面">
          <el-radio-group v-model="newSpec.side_type">
            <el-radio value="single">单面</el-radio>
            <el-radio value="double">双面</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="颜色">
          <el-radio-group v-model="newSpec.color_type">
            <el-radio value="black_white">黑白</el-radio>
            <el-radio value="color">彩色</el-radio>
          </el-radio-group>
        </el-form-item>
        <el-form-item label="价格">
          <el-input-number v-model="newSpec.price" :min="0" :precision="2" :step="0.1" />
        </el-form-item>
        <el-form-item label="会员价">
          <el-input-number v-model="newSpec.member_price" :min="0" :precision="2" :step="0.1" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="dialogVisible = false">取消</el-button>
        <el-button type="primary" @click="handleAdd">确定</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, onMounted } from 'vue'
import { ElMessage, ElMessageBox } from 'element-plus'
import request from '../../utils/request'

export default {
  name: 'PrintPrice',
  setup() {
    const priceList = ref([])
    const loading = ref(false)
    const dialogVisible = ref(false)
    const newSpec = ref({
      paper_size: 'A4',
      side_type: 'single',
      color_type: 'black_white',
      price: 0.5,
      member_price: 0.45
    })
    
    // 获取价格列表
    const fetchPrices = async () => {
      loading.value = true
      try {
        const res = await request.get('/api/print/prices')
        // 兼容后端返回格式
        const list = res.data?.list || res.data || []
        priceList.value = list.map(item => ({
          ...item,
          price: parseFloat(item.price) || 0,
          member_price: parseFloat(item.member_price) || 0
        }))
      } catch (error) {
        console.error('获取价格失败:', error)
        ElMessage.error('获取价格数据失败，请检查网络后重试')
        priceList.value = []
      } finally {
        loading.value = false
      }
    }
    
    // 保存全部
    const handleSaveAll = async () => {
      try {
        // 转换数据格式以兼容后端
        const prices = priceList.value.map(item => ({
          paper_size: item.paper_size,
          side_type: item.side_type,
          color_type: item.color_type,
          price: parseFloat(item.price),
          member_price: parseFloat(item.member_price)
        }))
        
        await request.post('/api/admin/print-prices', { prices })
        ElMessage.success('保存成功')
      } catch (error) {
        console.error('保存失败:', error)
        ElMessage.error('保存失败: ' + (error.message || '未知错误'))
      }
    }
    
    // 显示新增对话框
    const showAddDialog = () => {
      newSpec.value = {
        paper_size: 'A4',
        side_type: 'single',
        color_type: 'black_white',
        price: 0.5,
        member_price: 0.45
      }
      dialogVisible.value = true
    }
    
    // 新增规格
    const handleAdd = () => {
      // 检查是否已存在相同规格
      const exists = priceList.value.some(item => 
        item.paper_size === newSpec.value.paper_size &&
        item.side_type === newSpec.value.side_type &&
        item.color_type === newSpec.value.color_type
      )
      
      if (exists) {
        ElMessage.warning('该规格已存在')
        return
      }
      
      priceList.value.push({ ...newSpec.value })
      dialogVisible.value = false
      ElMessage.success('添加成功，请记得保存')
    }
    
    // 删除规格
    const handleDelete = async (index) => {
      try {
        await ElMessageBox.confirm('确定删除该规格吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        })
        priceList.value.splice(index, 1)
        ElMessage.success('删除成功，请记得保存')
      } catch {
        // 用户取消
      }
    }
    
    onMounted(() => {
      fetchPrices()
    })
    
    return {
      priceList,
      loading,
      dialogVisible,
      newSpec,
      fetchPrices,
      handleSaveAll,
      showAddDialog,
      handleAdd,
      handleDelete
    }
  }
}
</script>

<style scoped>
.print-price {
  padding: 8px;
}

.card-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 4px 0;
}

.card-header span {
  font-weight: 600;
  font-size: 16px;
  color: #303133;
}

/* 价格输入框 */
:deep(.el-input-number) {
  width: 120px;
}
</style>
